import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Copy, Check } from 'lucide-react';

const GAMBULL_LOGO = 'https://customer-assets.emergentagent.com/job_source-reveal-3/artifacts/g619rnzz_OIP__24_-removebg-preview.png';

// CSS for floating animations and interactive effects
const floatingStyles = `
  @keyframes float1 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-30px) rotate(5deg); }
  }
  @keyframes float2 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(25px) rotate(-5deg); }
  }
  @keyframes float3 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-25px) rotate(8deg); }
  }
  @keyframes float4 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(30px) rotate(-8deg); }
  }
  @keyframes float5 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(6deg); }
  }
  @keyframes float6 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(20px) rotate(-6deg); }
  }
  .float-img {
    position: absolute;
    width: 120px;
    height: 120px;
    object-fit: contain;
    pointer-events: auto;
    opacity: 0.7;
    filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.5));
    transition: transform 0.3s ease, filter 0.3s ease;
    cursor: pointer;
  }
  .float-img:hover {
    transform: scale(1.2) !important;
    filter: drop-shadow(0 0 30px rgba(168, 85, 247, 0.8)) drop-shadow(0 10px 30px rgba(0, 0, 0, 0.5));
    z-index: 100;
    opacity: 1;
  }
  .float-1 { top: 5%; left: 3%; animation: float1 6s ease-in-out infinite; }
  .float-2 { top: 5%; right: 3%; animation: float2 5s ease-in-out infinite; }
  .float-3 { top: 70%; left: 3%; animation: float3 7s ease-in-out infinite; }
  .float-4 { top: 70%; right: 3%; animation: float4 5.5s ease-in-out infinite; }
  .float-5 { top: 38%; left: 1%; animation: float5 6.5s ease-in-out infinite; }
  .float-6 { top: 38%; right: 1%; animation: float6 5s ease-in-out infinite; }
  
  /* Confetti explosion animation */
  @keyframes confetti-explode {
    0% { 
      transform: translate(0, 0) rotate(0deg) scale(0);
      opacity: 1;
    }
    20% {
      transform: translate(var(--x), var(--y)) rotate(180deg) scale(1.5);
      opacity: 1;
    }
    100% { 
      transform: translate(calc(var(--x) * 3), calc(var(--y) * 3 + 200px)) rotate(720deg) scale(0.5);
      opacity: 0;
    }
  }
  .confetti-piece {
    position: fixed;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    animation: confetti-explode 1.5s ease-out forwards;
    z-index: 9999;
    pointer-events: none;
  }
  .confetti-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 9999;
  }
`;

// Particle component
const Particles = () => {
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 15,
    duration: 15 + Math.random() * 20,
    size: 2 + Math.random() * 4,
  }));

  return (
    <div className="particles-container">
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [counter, setCounter] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const counterRef = useRef(null);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText('https://gambulls.com/?ref=shadingo');
    } catch (err) {
      console.log('Clipboard not available');
    }
    setCopied(true);
    setShowConfetti(true);
    setTimeout(() => {
      setCopied(false);
      setShowConfetti(false);
    }, 2000);
  };

  // Intersection Observer for counter animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounter();
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCounter = () => {
    const target = 35921;
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCounter(target);
        clearInterval(timer);
      } else {
        setCounter(Math.floor(current));
      }
    }, duration / steps);
  };

  // Generate explosive confetti pieces
  const confettiPieces = showConfetti ? Array.from({ length: 50 }, (_, i) => {
    const angle = (i / 50) * 360;
    const velocity = 50 + Math.random() * 150;
    const x = Math.cos(angle * Math.PI / 180) * velocity;
    const y = Math.sin(angle * Math.PI / 180) * velocity - 100;
    return {
      id: i,
      x,
      y,
      color: ['#a855f7', '#ec4899', '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#00FF00'][Math.floor(Math.random() * 7)],
      delay: Math.random() * 0.2,
      size: 8 + Math.random() * 8,
      shape: Math.random() > 0.5 ? 'square' : 'circle',
    };
  }) : [];


  return (
    <>
      {/* Embedded CSS for floating animations */}
      <style>{floatingStyles}</style>
      
      {/* First Section - Hero */}
      <div className="min-h-screen hero-gradient flex items-center justify-center relative overflow-hidden">
        {/* Particles Background */}
        <Particles />
        
        {/* Gradient Orbs - purple theme */}
        <div className="absolute top-0 left-0 w-48 md:w-96 h-48 md:h-96 bg-purple-500/30 rounded-full blur-3xl"></div>
        <div className="absolute top-0 right-0 w-48 md:w-96 h-48 md:h-96 bg-pink-500/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 md:w-96 h-48 md:h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-48 md:w-96 h-48 md:h-96 bg-pink-600/20 rounded-full blur-3xl"></div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 md:px-6 max-w-4xl mx-auto pt-24 md:pt-32">
          {/* Logo with Glow */}
          <div className="mb-6 md:mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_551626b0-f9c7-4868-8db1-d6bfdfbb1a20/artifacts/uqdo6djg_Copilot_20260203_160408.png" 
              alt="Shadingo Rewards" 
              className="w-full max-w-xs md:max-w-2xl mx-auto logo-glow"
            />
          </div>

          {/* Description Text */}
          <p className="text-white text-lg md:text-2xl lg:text-3xl leading-relaxed mb-8 md:mb-12 font-exo font-light px-2">
            Discover the world of VIP Rewards with Shadingo!
            <br className="hidden md:block" />
            <span className="md:hidden"> </span>
            Climb the{' '}
            <button
              onClick={() => navigate('/leaderboard')}
              className="text-purple-400 hover:text-purple-300 font-semibold underline decoration-purple-400/50 hover:decoration-purple-300 underline-offset-4 transition-all duration-300 cursor-pointer inline-block hover:scale-105"
            >
              Leaderboards
            </button>
            , test your luck in Exclusive Raffles, participate in Giveaways, and more!
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-center mb-8 md:mb-16">
            <Button 
              onClick={() => navigate('/leaderboard')}
              size="lg"
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white px-8 md:px-10 py-4 md:py-6 rounded-full text-lg md:text-xl font-bold transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/50 hover:scale-105 border-0 w-full sm:w-auto sm:min-w-[200px]"
            >
              Leaderboard
            </Button>
            
            <Button 
              onClick={() => window.open('https://discord.gg/a8BeeyTPSr', '_blank')}
              size="lg"
              className="bg-[#5865F2] hover:bg-[#4752C4] text-white px-8 md:px-10 py-4 md:py-6 rounded-full text-lg md:text-xl font-bold transition-all duration-300 hover:shadow-2xl hover:shadow-[#5865F2]/50 hover:scale-105 border-0 w-full sm:w-auto sm:min-w-[200px] flex items-center justify-center gap-3"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 md:w-6 md:h-6" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Join Discord
            </Button>
          </div>

          {/* Scroll Mouse Indicator - hidden on mobile */}
          <div className="mouse-scroll-indicator hidden md:block">
            <div className="mouse">
              <div className="mouse-wheel"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Second Section - Code Section */}
      <div className="min-h-screen blue-gradient-section relative overflow-hidden flex items-center justify-center py-12 md:py-20" style={{ zIndex: 1 }}>
        {/* Particles in second section too */}
        <Particles />
        
        {/* Floating Elements - hidden on mobile for better performance */}
        <img 
          src="https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/ff80kytq_zeus.png" 
          alt="Zeus" 
          className="float-img float-1 hidden md:block"
          style={{ zIndex: 2 }}
        />
        <img 
          src="https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/08vojbo9_ring.png" 
          alt="Ring" 
          className="float-img float-2 hidden md:block"
          style={{ zIndex: 2 }}
        />
        <img 
          src="https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/4093szkr_crown%20%281%29.png" 
          alt="Crown" 
          className="float-img float-3 hidden md:block"
          style={{ zIndex: 2 }}
        />
        <img 
          src="https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/cick3r3k_dog.png" 
          alt="Dog" 
          className="float-img float-4 hidden md:block"
          style={{ zIndex: 2 }}
        />
        <img 
          src="https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/3kgzvuyl_bonus.png" 
          alt="Bonus" 
          className="float-img float-5 hidden md:block"
          style={{ zIndex: 2 }}
        />
        <img 
          src="https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/8mohpwpa_bomb.png" 
          alt="Bomb" 
          className="float-img float-6 hidden md:block"
          style={{ zIndex: 2 }}
        />

        {/* Content */}
        <div className="relative z-10 text-center px-4 md:px-6 max-w-4xl mx-auto py-6 md:py-10">
          {/* Title */}
          <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-orbitron font-bold text-white mb-6 md:mb-12">
            <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-purple-600 bg-clip-text text-transparent">MOST REWARDING</span>
            <div className="block md:inline-block mx-2 md:mx-4 my-2 md:my-0 align-middle">
              <img 
                src={GAMBULL_LOGO} 
                alt="GamBull" 
                className="h-10 sm:h-12 md:h-16 lg:h-20 inline-block"
              />
            </div>
            <span className="text-white">CODE</span>
          </h2>

          {/* Confetti explosion container */}
          {showConfetti && (
            <div className="confetti-container">
              {confettiPieces.map((piece) => (
                <div
                  key={piece.id}
                  className="confetti-piece"
                  style={{
                    '--x': `${piece.x}px`,
                    '--y': `${piece.y}px`,
                    backgroundColor: piece.color,
                    animationDelay: `${piece.delay}s`,
                    width: `${piece.size}px`,
                    height: `${piece.size}px`,
                    borderRadius: piece.shape === 'circle' ? '50%' : '2px',
                  }}
                />
              ))}
            </div>
          )}

          {/* Code Display with Wood Background */}
          <div className="ref-code-container mb-6 md:mb-8">
            <div className="ref-code-wood">
              <div className="ref-code-content relative">
                <strong className="ref-code-value font-orbitron">SHADINGO</strong>
                <button 
                  onClick={handleCopyCode}
                  className="ref-code-btn"
                  aria-label="Copy code"
                >
                  {copied ? <Check className="w-6 h-6" /> : <Copy className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>

          {/* Description Text */}
          <p className="text-white text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 leading-relaxed font-exo px-2">
            Use code <span className="text-purple-400 font-bold">"SHADINGO"</span> on GamBull to support our code and{' '}
            <span className="text-purple-400 font-bold">take part in our $250 Monthly Leaderboard</span>!
          </p>

          {/* Join Button */}
          <Button 
            onClick={() => window.open('https://gambulls.com/?ref=shadingo', '_blank')}
            size="lg"
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white px-8 md:px-12 py-4 md:py-6 rounded-full text-lg md:text-xl font-orbitron font-bold transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/50 hover:scale-105 border-0 mb-10 md:mb-20"
          >
            JOIN NOW
          </Button>

          {/* Money Bags Image */}
          <div className="mb-6 md:mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/pjt4kdnd_3-bags.svg" 
              alt="Money Bags" 
              className="w-40 md:w-64 mx-auto hover:scale-110 transition-transform duration-300 cursor-pointer"
            />
          </div>

          {/* Total Given Away */}
          <div ref={counterRef} className="mb-10 md:mb-16">
            <h3 className="text-white text-xl md:text-2xl font-exo font-light mb-3 md:mb-4">Total<br />Given away</h3>
            <p className="bg-gradient-to-r from-purple-400 via-pink-500 to-purple-600 bg-clip-text text-transparent text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-oxanium font-bold tracking-wider">
              ${counter.toLocaleString()}
            </p>
          </div>

          {/* Follow Socials */}
          <div className="mb-10 md:mb-20">
            <h3 className="text-white text-xl md:text-2xl font-exo font-light mb-4 md:mb-6">Follow our<br />Socials</h3>
            <div className="flex gap-4 md:gap-6 justify-center items-center flex-wrap">
              {/* Kick Logo */}
              <a 
                href="https://kick.com/shadingo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-link"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#53FC18] rounded-xl md:rounded-2xl flex items-center justify-center hover:scale-110 transition-all duration-300 hover:shadow-2xl hover:shadow-[#53FC18]/50">
                  <svg viewBox="0 0 32 32" className="w-7 h-7 md:w-10 md:h-10" fill="black">
                    <path d="M11.25 6H7v20h4.25v-7.5L16 24h5.5l-6.75-8.5L21.5 8H16l-4.75 5.5V6z"/>
                  </svg>
                </div>
              </a>
              
              {/* Discord Logo */}
              <a 
                href="https://discord.gg/a8BeeyTPSr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-link"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#5865F2] rounded-xl md:rounded-2xl flex items-center justify-center hover:scale-110 transition-all duration-300 hover:shadow-2xl hover:shadow-[#5865F2]/50">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 md:w-10 md:h-10" fill="white">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                </div>
              </a>

              {/* Instagram Logo */}
              <a 
                href="https://www.instagram.com/shadingo_" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-link"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#dc2743] rounded-xl md:rounded-2xl flex items-center justify-center hover:scale-110 transition-all duration-300 hover:shadow-2xl hover:shadow-[#e6683c]/50">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 md:w-10 md:h-10" fill="white">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
              </a>

              {/* TikTok Logo */}
              <a 
                href="https://www.tiktok.com/@shadijoaan" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-link"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-black rounded-xl md:rounded-2xl flex items-center justify-center hover:scale-110 transition-all duration-300 hover:shadow-2xl hover:shadow-white/30 border border-white/20">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 md:w-10 md:h-10" fill="white">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>
              </a>

              {/* X / Twitter */}
              <a 
                href="https://x.com/shadingo_kick" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-link"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-black rounded-xl md:rounded-2xl flex items-center justify-center hover:scale-110 transition-all duration-300 hover:shadow-2xl hover:shadow-white/30 border border-white/20">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 md:w-8 md:h-8" fill="white">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black/80 border-t border-purple-500/20 py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-start">
            {/* Logo */}
            <div className="flex justify-center md:justify-start">
              <img 
                src="https://customer-assets.emergentagent.com/job_551626b0-f9c7-4868-8db1-d6bfdfbb1a20/artifacts/uqdo6djg_Copilot_20260203_160408.png" 
                alt="Shadingo Rewards" 
                className="h-14 md:h-20 w-auto"
              />
            </div>

            {/* Pages */}
            <div className="text-center">
              <h4 className="text-purple-400 font-semibold text-base md:text-lg mb-3 md:mb-4">Pages</h4>
              <div className="space-y-2">
                <button 
                  onClick={() => navigate('/')}
                  className="block w-full text-white/70 hover:text-purple-400 transition-colors duration-300 text-sm md:text-base"
                >
                  Home
                </button>
                <button 
                  onClick={() => navigate('/leaderboard')}
                  className="block w-full text-white/70 hover:text-purple-400 transition-colors duration-300 text-sm md:text-base"
                >
                  Leaderboard
                </button>
              </div>
            </div>

            {/* Made By */}
            <div className="text-center md:text-right">
              <p className="text-white/50 mb-2 text-sm md:text-base">Made By</p>
              <a 
                href="https://raff.buzz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 font-semibold text-base md:text-lg transition-all duration-300 hover:underline inline-block group"
                title="Visit raff.buzz"
              >
                raffdevs
                <span className="block text-xs md:text-sm text-white/40 group-hover:text-white/60 transition-colors">
                  Click to visit
                </span>
              </a>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-purple-500/10 text-center">
            <p className="text-white/40 text-xs md:text-sm">
              &copy; 2025 Shadingo Rewards. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Landing;
